import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  User,
  Building2,
  Dumbbell,
  Mail,
  Lock,
  Eye,
  EyeOff,
  MapPin,
  ShieldCheck,
  RefreshCw,
  Phone,
  Briefcase,
  ChevronDown,
  X,
  UserCircle,
} from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import PhoneAuthModal from "@/components/PhoneAuthModal";


export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['auth', 'common', "admin", "surveys"]);
  const { getLocalizedBranch } = useLocalizedData();
  const [isLogin, setIsLogin] = useState(searchParams.get("mode") !== "register");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(searchParams.get("type") || "member");

  // Verification step
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // DEV: Skip email verification for testing
  const [skipVerification, setSkipVerification] = useState(false);
  
  // Phone auth modal
  const [showPhoneAuth, setShowPhoneAuth] = useState(false);
  const [phoneRegistrationData, setPhoneRegistrationData] = useState(null);
  
  // Auth method: "email" or "phone"
  const [authMethod, setAuthMethod] = useState("email");
  
  // Remember me
  const [rememberMe, setRememberMe] = useState(false);
  // Forgot Password
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSentTo, setForgotSentTo] = useState(null);
  
  // Remembered users - stores array of {email, password, name} objects
  const [rememberedUsers, setRememberedUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Form states - initialized from localStorage if remember me was set
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [gender, setGender] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [phone, setPhone] = useState("");
  
  // Initialize remembered users from localStorage + migrate from old format
  useEffect(() => {
    try {
      // First check for new format
      const saved = localStorage.getItem('remembered_users');
      if (saved) {
        const users = JSON.parse(saved);
        if (Array.isArray(users) && users.length > 0) {
          setRememberedUsers(users);
          setRememberMe(true);
          // Auto-fill with the first (most recent) user
          setEmail(users[0].email);
          setPassword(users[0].password);
          return;
        }
      }
      
      // Migration from old single-user format
      const oldEmail = localStorage.getItem('remembered_email');
      const oldPassword = localStorage.getItem('remembered_password');
      if (oldEmail && oldPassword) {
        const migratedUser = { email: oldEmail, password: oldPassword, name: oldEmail.split('@')[0] };
        setRememberedUsers([migratedUser]);
        setRememberMe(true);
        setEmail(oldEmail);
        setPassword(oldPassword);
        // Save to new format and clean up old keys
        localStorage.setItem('remembered_users', JSON.stringify([migratedUser]));
        localStorage.removeItem('remembered_email');
        localStorage.removeItem('remembered_password');
        localStorage.removeItem('remember_me');
      }
    } catch (e) {
      console.error('Error loading remembered users:', e);
    }
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Select a remembered user
  const handleSelectRememberedUser = (user) => {
    if (user.authMethod === "phone" || user.phone) {
      setAuthMethod("phone");
      setPhone(user.phone || user.identifier);
    } else {
      setAuthMethod("email");
      setEmail(user.email || user.identifier);
    }
    setPassword(user.password);
    setShowUserDropdown(false);
  };
  
  // Remove a remembered user
  const handleRemoveRememberedUser = (e, userEmail) => {
    e.stopPropagation();
    const updatedUsers = rememberedUsers.filter(u => u.email !== userEmail);
    setRememberedUsers(updatedUsers);
    if (updatedUsers.length > 0) {
      localStorage.setItem('remembered_users', JSON.stringify(updatedUsers));
    } else {
      localStorage.removeItem('remembered_users');
      setRememberMe(false);
    }
    // Şifreyi de temizle
    setPassword("");
    toast.success(t("common:toast_kullaniciListedenKaldirildi"));
  };
  
  // Clear email and password fields
  const handleClearEmail = () => {
    setEmail("");
    setPassword("");
    setShowUserDropdown(false);
  };
  
  // Trainer specific - known branches
  const [knownBranches, setKnownBranches] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);

  // Fetch available branches on mount
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await fetch(`${API}/public/branches`);
        if (response.ok) {
          const data = await response.json();
          // Normalize branches
          const normalized = (Array.isArray(data) ? data : []).map(b => {
            if (typeof b === 'string') return { id: b, name: b, is_active: true };
            return b;
          }).filter(b => b.is_active !== false);
          setAvailableBranches(normalized);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };
    fetchBranches();
  }, []);

  // Clear form when switching between login and register - but keep remembered credentials
  useEffect(() => {
    // If switching to login and we have remembered users, auto-fill
    if (isLogin && rememberedUsers.length > 0) {
      setEmail(rememberedUsers[0].email);
      setPassword(rememberedUsers[0].password);
    } else if (!isLogin) {
      // Clear for registration
      setEmail("");
      setPassword("");
    }
    setName("");
    setSurname("");
    setGender("");
    setBirthDay("");
    setBirthMonth("");
    setBirthYear("");
    setPhone("");
    setKnownBranches([]);
    setShowPassword(false);
    setShowVerification(false);
    setVerificationCode("");
    setPendingEmail("");
    setShowUserDropdown(false);
  }, [isLogin]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        let response;
        let responseData;
        
        // Determine login endpoint based on authMethod
        const loginEndpoint = authMethod === "phone" ? `${API}/auth/login/phone` : `${API}/auth/login`;
        const loginBody = authMethod === "phone" 
          ? { phone, password }
          : { email, password };
        
        try {
          response = await fetch(loginEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(loginBody),
          });
          
          // Try to parse response - handle already-read body gracefully
          try {
            responseData = await response.json();
          } catch (jsonError) {
            // If body was already read (e.g., by service worker or interceptor),
            // we can still check status code for basic error handling
            console.warn("Could not parse response body:", jsonError);
            
            if (!response.ok) {
              // Use status code to determine error message
              if (response.status === 401) {
                toast.error(t("common:toast_epostaVeyaSifreHatali"));
              } else if (response.status === 403) {
                toast.error(t("common:toast_epostaAdresinizDogrulanmamis"));
              } else if (response.status === 404) {
                toast.error(t("common:toast_buEpostaIleKayitliKullanici"));
              } else {
                toast.error(t("common:toast_girisBasarisizLutfenTekrarDeneyin"));
              }
              return;
            }
            // If response was OK but couldn't parse, treat as error
            toast.error(t("surveys:toast_sunucuYanitiIslenemedi"));
            return;
          }
        } catch (fetchError) {
          console.error("Login fetch error:", fetchError);
          toast.error(t("common:toast_sunucuyaBaglanilamadiLutfenTekrarDeneyin"));
          return;
        }
        
        if (!response.ok) {
          // Backend already returns Turkish error messages, use them directly
          const errorMessage = responseData?.detail || t("common:loginFailed");
          toast.error(errorMessage);
          return;
        }
        
        // Successful login - use responseData instead of data
        const data = responseData;

        // Save/clear remember me credentials - now supports multiple users
        if (rememberMe) {
          const userName = data.user?.name || data.name || (authMethod === "phone" ? phone : email.split('@')[0]);
          const identifier = authMethod === "phone" ? phone : email;
          
          // Listede aynı telefon ya da email varsa temizle
          const existingUsers = rememberedUsers.filter(u => (u.identifier || u.email) !== identifier);
          const newUserEntry = { 
            identifier, 
            email: authMethod === "email" ? email : "", 
            phone: authMethod === "phone" ? phone : "",
            authMethod,
            password, 
            name: userName 
          };
          const updatedUsers = [newUserEntry, ...existingUsers].slice(0, 5);
          
          setRememberedUsers(updatedUsers);
          localStorage.setItem('remembered_users', JSON.stringify(updatedUsers));
        }
        // Note: We don't remove from list if rememberMe is unchecked - user can manually remove

        // Save token to localStorage for cross-origin API calls
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
        }

        toast.success(t('auth:loginSuccess'), { duration: 2000 });
        
        // Check if business user has a business
        if (data.user?.role === "business") {
          try {
            const token = data.token;
            const bizResponse = await fetch(`${API}/my-businesses`, {
              headers: token ? { "Authorization": `Bearer ${token}` } : {}
            });
            
            if (bizResponse.ok) {
              const businesses = await bizResponse.json();
              
              if (!businesses || businesses.length === 0) {
                navigate("/register-business", { state: { user: data.user, firstTime: true } });
                return;
              }
            }
          } catch (e) {
            console.error("Error checking businesses:", e);
          }
        }
        
        navigate("/", { state: { user: data.user } });
      } else {
        // Validate all required fields for registration
        if (!name.trim()) {
          throw new Error("Ad zorunludur");
        }
        if (!surname.trim()) {
          throw new Error("Soyad zorunludur");
        }
        if (!gender) {
          throw new Error(t('auth:genderRequired') || "Gender is required");
        }
        if (!birthYear || !birthMonth || !birthDay) {
          throw new Error(t('auth:birthDateRequired'));
        }
        
        // Validation based on authMethod and userType
        if (authMethod === "phone") {
          // Phone auth: phone required, email required only for business
          if (!phone.trim()) {
            throw new Error(t('auth:phoneRequired'));
          }
          if (userType === "business" && !email.trim()) {
            throw new Error(t('auth:businessEmailRequired'));
          }
        } else {
          // Email auth: email required, phone required only for business
          if (!email.trim()) {
            throw new Error(t('auth:emailRequired'));
          }
          if (userType === "business" && !phone.trim()) {
            throw new Error(t('auth:businessPhoneRequired'));
          }
        }
        
        if (!password || password.length < 6) {
          throw new Error(t('auth:passwordMinLength'));
        }
        
        // Trainer specific validation - at least 1 branch required
        if (userType === "trainer" && knownBranches.length === 0) {
          throw new Error(t('auth:trainerBranchRequired'));
        }
        
        const birthDate = `${birthYear}-${birthMonth}-${birthDay}`;
        
        // Check if email or phone already exists BEFORE registration
        if (email) {
          try {
            const emailCheckResponse = await fetch(`${API}/auth/check-email?email=${encodeURIComponent(email)}`);
            const emailCheckData = await emailCheckResponse.json();
            if (emailCheckData.exists) {
              throw new Error(t('auth:emailAlreadyRegistered'));
            }
          } catch (checkError) {
            if (checkError.message.includes("already") || checkError.message.includes("zaten")) {
              throw checkError;
            }
            // Ignore network errors for check - let backend handle it
          }
        }
        
        if (phone) {
          try {
            const phoneCheckResponse = await fetch(`${API}/auth/check-phone?phone=${encodeURIComponent(phone)}`);
            const phoneCheckData = await phoneCheckResponse.json();
            if (phoneCheckData.exists) {
              throw new Error(t('auth:phoneAlreadyRegistered'));
            }
          } catch (checkError) {
            if (checkError.message.includes("already") || checkError.message.includes("zaten")) {
              throw checkError;
            }
            // Ignore network errors for check - let backend handle it
          }
        }
        
        // Build registration payload based on authMethod
        const registrationPayload = {
          name,
          surname,
          gender,
          birth_date: birthDate,
          phone: phone || null,
          email: email || null,
          role: userType,
          known_branches: userType === "trainer" ? knownBranches : [],
          password,
        };
        
        // DEV: Skip verification if checkbox is checked
        if (skipVerification) {
          const response = await fetch(`${API}/auth/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({...registrationPayload, email: registrationPayload.email || `temp_${Date.now()}@noemail.com`}),
          });

          const data = await response.json();
          
          if (!response.ok) {
            throw new Error(data?.detail || t('auth:registerFailed'));
          }

          // Save token to localStorage for cross-origin requests
          if (data.token) {
            localStorage.setItem("auth_token", data.token);
          }

          toast.success(t('auth:registerSuccess'), { duration: 2000 });
          
          // If business user, redirect to register business page
          if (userType === "business") {
            navigate("/register-business", { state: { user: data, token: data.token, firstTime: true } });
          } else {
            navigate("/", { state: { user: data } });
          }
          return;
        }
        
        // PHONE AUTH: Use Firebase Phone Auth via modal
        if (authMethod === "phone") {
          const birthDate = `${birthYear}-${birthMonth}-${birthDay}`;
          
          // Store registration data and open phone auth modal
          setPhoneRegistrationData({
            phone: phone,
            name: name,
            surname: surname,
            gender: gender,
            birth_date: birthDate,
            email: email || null,
            role: userType,
            known_branches: userType === "trainer" ? knownBranches : [],
            password: password,
          });
          setShowPhoneAuth(true);
          setLoading(false);
          return;
        }
          
        // EMAIL AUTH: Normal flow - Request registration (sends email verification code)
        const response = await fetch(`${API}/auth/register/request`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(registrationPayload),
        });

        // Handle error responses properly
        if (!response.ok) {
          let errorMessage = t('auth:registerFailed');
          try {
            const errorData = await response.json();
            errorMessage = errorData?.detail || errorMessage;
          } catch (parseError) {
            console.error("Error parsing response:", parseError);
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        // Show verification step for email auth
        setPendingEmail(email);
        setShowVerification(true);
        setResendCooldown(60); // 60 second cooldown
        toast.success(t('auth:verificationCodeSentToEmail'), { duration: 4000 });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Email verification endpoint
      const response = await fetch(`${API}/auth/register/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: pendingEmail, code: verificationCode }),
      });

      // Handle error responses properly
      if (!response.ok) {
        let errorMessage = t('auth:verificationFailed');
        try {
          const errorData = await response.json();
          errorMessage = errorData?.detail || errorMessage;
        } catch (parseError) {
          console.error("Error parsing response:", parseError);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Save token to localStorage for cross-origin requests
      if (data.token) {
        localStorage.setItem("auth_token", data.token);
      }

      toast.success(t('auth:registerSuccessWelcome'), { duration: 2000 });
      
      // If business user, redirect to register business page
      if (userType === "business" || data?.role === "business") {
        navigate("/register-business", { state: { user: data, token: data.token, firstTime: true } });
      } else {
        navigate("/", { state: { user: data } });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/register/resend-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: pendingEmail }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data?.detail || t('auth:codeNotSent'));
      }

      setResendCooldown(60);
      toast.success(t('auth:newVerificationSent'), { duration: 3000 });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy-600 to-navy-800 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-500 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Explore Map Button */}
        <Link
          to="/explore"
          className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
        >
          <MapPin className="w-4 h-4" />
          {t('common:exploreMap')}
        </Link>

        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center mx-auto mb-6">
              {showVerification ? (
                <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
              ) : (
                <img 
                  src="https://customer-assets.emergentagent.com/job_273f8795-754a-4865-8c8d-c4895c64dfec/artifacts/80de64xp_output-onlinepngtools.png" 
                  alt="Probody Studio Logo" 
                  className="w-28 h-28 object-contain"
                />
              )}
            </div>
            <CardTitle className="font-heading text-2xl text-navy">
              {showVerification ? t('auth:verificationCode') : isLogin ? t('auth:welcomeTitle') : t('auth:registerTab')}
            </CardTitle>
            <CardDescription>
              {showVerification
                ? t('auth:enterCodeSent', { email: pendingEmail }) || `${pendingEmail} adresine gönderilen kodu girin`
                : isLogin
                ? t('auth:welcomeSubtitle')
                : t('auth:welcomeRegisterSubtitle')}
            </CardDescription>
            <div className="flex justify-center mt-2">
              <LanguageSwitcher />
            </div>
          </CardHeader>

          <CardContent className="pt-4">
            {/* Verification Step */}
            {showVerification ? (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <p className="text-sm text-blue-800">
                    {t('auth:verificationCodeSent', { email: pendingEmail }) || `📧 ${pendingEmail} adresine 6 haneli doğrulama kodu gönderdik.`}
                  </p>
                </div>

                <div>
                  <Label htmlFor="code">{t("admin:verificationCode")}</Label>
                  <Input
                    id="code"
                    type="text"
                    maxLength={6}
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    className="h-14 text-center text-2xl tracking-[0.5em] font-mono rounded-xl"
                    data-testid="verification-code-input"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-navy hover:bg-navy/90 rounded-xl"
                  disabled={loading || verificationCode.length !== 6}
                  data-testid="verify-code-btn"
                >
                  {loading ? t('auth:verifying') : t('auth:verifyAndRegister')}
                </Button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setShowVerification(false);
                      setVerificationCode("");
                    }}
                    className="text-navy hover:underline"
                  >
                    ← Geri Dön
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resendCooldown > 0 || loading}
                    className={`flex items-center gap-1 ${
                      resendCooldown > 0 ? "text-grey" : "text-navy hover:underline"
                    }`}
                  >
                    <RefreshCw className="w-4 h-4" />
                    {resendCooldown > 0 ? `${resendCooldown}s` : t('auth:resend')}
                  </button>
                </div>
              </form>
            ) : (
              <>
                {/* Auth Method Tabs - Email or Phone */}
                <div className="flex rounded-xl bg-slate-100 p-1 mb-6">
                  <button
                    type="button"
                    onClick={() => setAuthMethod("email")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                      authMethod === "email"
                        ? "bg-white text-navy shadow-sm"
                        : "text-grey hover:text-navy"
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    {t('auth:emailTab')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthMethod("phone")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                      authMethod === "phone"
                        ? "bg-white text-navy shadow-sm"
                        : "text-grey hover:text-navy"
                    }`}
                  >
                    <Phone className="w-4 h-4" />
                    {t('auth:phoneTab')}
                  </button>
                </div>

                {/* User Type Selection (only for registration) */}
                {!isLogin && (
                  <div className="mb-6">
                <Label className="text-sm font-medium text-grey-700 mb-2 block">
                  {t('auth:accountType')}
                </Label>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    type="button"
                    data-testid="type-member"
                    className={`p-2 rounded-xl border-2 transition-all ${
                      userType === "member"
                        ? "border-navy bg-navy/5"
                        : "border-grey-200 hover:border-grey-300"
                    }`}
                    onClick={() => setUserType("member")}
                  >
                    <User className={`w-5 h-5 mx-auto mb-1 ${userType === "member" ? "text-navy" : "text-grey"}`} />
                    <span className={`text-xs font-medium ${userType === "member" ? "text-navy" : "text-grey"}`}>
                      {t('auth:member')}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid="type-trainer"
                    className={`p-2 rounded-xl border-2 transition-all ${
                      userType === "trainer"
                        ? "border-navy bg-navy/5"
                        : "border-grey-200 hover:border-grey-300"
                    }`}
                    onClick={() => setUserType("trainer")}
                  >
                    <Dumbbell className={`w-5 h-5 mx-auto mb-1 ${userType === "trainer" ? "text-navy" : "text-grey"}`} />
                    <span className={`text-xs font-medium ${userType === "trainer" ? "text-navy" : "text-grey"}`}>
                      {t('auth:trainer')}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid="type-business"
                    className={`p-2 rounded-xl border-2 transition-all ${
                      userType === "business"
                        ? "border-navy bg-navy/5"
                        : "border-grey-200 hover:border-grey-300"
                    }`}
                    onClick={() => setUserType("business")}
                  >
                    <Building2 className={`w-5 h-5 mx-auto mb-1 ${userType === "business" ? "text-navy" : "text-grey"}`} />
                    <span className={`text-xs font-medium ${userType === "business" ? "text-navy" : "text-grey"}`}>
                      {t('auth:business')}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid="type-employee"
                    className={`p-2 rounded-xl border-2 transition-all ${
                      userType === "employee"
                        ? "border-navy bg-navy/5"
                        : "border-grey-200 hover:border-grey-300"
                    }`}
                    onClick={() => setUserType("employee")}
                  >
                    <Briefcase className={`w-5 h-5 mx-auto mb-1 ${userType === "employee" ? "text-navy" : "text-grey"}`} />
                    <span className={`text-xs font-medium ${userType === "employee" ? "text-navy" : "text-grey"}`}>
                      {t('auth:employee')}
                    </span>
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleEmailAuth} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="name">{t("common:firstName")}</Label>
                      <Input
                        id="name"
                        data-testid="name-input"
                        placeholder={t("common:ph_adiniz")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-11 rounded-xl"
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <Label htmlFor="surname">{t('common:surname')}</Label>
                      <Input
                        id="surname"
                        data-testid="surname-input"
                        placeholder={t("common:ph_soyadiniz")}
                        value={surname}
                        onChange={(e) => setSurname(e.target.value)}
                        className="h-11 rounded-xl"
                        required
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {/* Cinsiyet - Ayrı satır */}
                  <div>
                    <Label htmlFor="gender">{t('common:gender')}</Label>
                    <Select value={gender} onValueChange={setGender}>
                      <SelectTrigger data-testid="gender-select" className="h-11 rounded-xl">
                        <SelectValue placeholder={t("common:ph_secin")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{t('common:male')}</SelectItem>
                        <SelectItem value="female">{t('common:female')}</SelectItem>
                        <SelectItem value="other">{t('common:other')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Doğum Tarihi - Ayrı satır */}
                  <div>
                    <Label>{t('members:birthDate')}</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Select value={birthDay} onValueChange={setBirthDay}>
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue placeholder={t("common:day")}>{birthDay || "Gün"}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => (
                            <SelectItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                              {i + 1}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={birthMonth} onValueChange={setBirthMonth}>
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue placeholder={t("common:month")}>
                            {birthMonth ? [t('auth:monthShortJan'), t('auth:monthShortFeb'), t('auth:monthShortMar'), t('auth:monthShortApr'), t('auth:monthShortMay'), t('auth:monthShortJun'), t('auth:monthShortJul'), t('auth:monthShortAug'), t('auth:monthShortSep'), t('auth:monthShortOct'), t('auth:monthShortNov'), t('auth:monthShortDec')][parseInt(birthMonth) - 1] : t("common:month")}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {[t('auth:monthJan'), t('auth:monthFeb'), t('auth:monthMar'), t('auth:monthApr'), t('auth:monthMay'), t('auth:monthJun'), t('auth:monthJul'), t('auth:monthAug'), t('auth:monthSep'), t('auth:monthOct'), t('auth:monthNov'), t('auth:monthDec')].map((month, i) => (
                            <SelectItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                              {month}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={birthYear} onValueChange={setBirthYear}>
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue placeholder={t("common:year")}>{birthYear || t("common:year")}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 80 }, (_, i) => {
                            const year = new Date().getFullYear() - 10 - i;
                            return (
                              <SelectItem key={year} value={String(year)}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Phone field */}
                  <div>
                    <Label htmlFor="phone">
                      {t('auth:phone')} {(authMethod === "phone" || userType === "business") && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      placeholder="05XX XXX XX XX"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="h-11 rounded-xl"
                      required={authMethod === "phone" || userType === "business"}
                    />
                  </div>
                  
                  {/* Email field */}
                  <div>
                    <Label htmlFor="email">
                      {t('auth:email')} {(authMethod === "email" || userType === "business") && <span className="text-red-500">*</span>}
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
                      <Input
                        id="email"
                        type="email"
                        data-testid="email-input"
                        placeholder="ornek@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-11 rounded-xl pl-10"
                        required={authMethod === "email" || userType === "business"}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  
                  {/* Trainer Branch Selection */}
                  {userType === "trainer" && (
                    <div>
                      <Label className="flex items-center gap-2">
                        <Dumbbell className="w-4 h-4" />
                        {t('auth:knownBranches')} <span className="text-red-500">*</span>
                      </Label>
                      <p className="text-xs text-grey mb-2">
                        {t('auth:knownBranchesDesc')}
                      </p>
                      <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-xl border">
                        {availableBranches.length > 0 ? (
                          availableBranches.map((branch) => (
                            <button
                              key={branch.id || branch.name}
                              type="button"
                              data-testid={`branch-${branch.name}`}
                              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                                knownBranches.includes(branch.name)
                                  ? "bg-navy text-white"
                                  : "bg-white text-grey-700 border hover:border-navy hover:text-navy"
                              }`}
                              onClick={() => {
                                setKnownBranches(prev =>
                                  prev.includes(branch.name)
                                    ? prev.filter(b => b !== branch.name)
                                    : [...prev, branch.name]
                                );
                              }}
                            >
                              {getLocalizedBranch(branch.name)}
                            </button>
                          ))
                        ) : (
                          <p className="text-sm text-grey">{t('auth:branchesLoading')}</p>
                        )}
                      </div>
                      {knownBranches.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          <span className="text-xs text-grey">{t('auth:selected')}:</span>
                          {knownBranches.map(b => (
                            <Badge key={b} variant="secondary" className="text-xs">
                              {getLocalizedBranch(b)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Email Alanı - sadece login modunda ve email auth seçiliyse */}
              {isLogin && authMethod === "email" && (
              <div>
                <Label htmlFor="email">{t('common:email')}</Label>
                <div className="relative" ref={dropdownRef}>
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey z-10" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder={t('auth:emailPlaceholder')}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setShowUserDropdown(false);
                    }}
                    onFocus={() => {
                      if (isLogin && rememberedUsers.length > 0) {
                        setShowUserDropdown(true);
                      }
                    }}
                    className={`h-11 pl-10 rounded-xl ${isLogin && rememberedUsers.length > 0 ? 'pr-20' : 'pr-10'}`}
                    required
                    autoComplete="off"
                  />
                  {/* Clear email button - shows when email has value */}
                  {email && (
                    <button
                      type="button"
                      data-testid="clear-email-btn"
                      onClick={handleClearEmail}
                      className={`absolute top-1/2 -translate-y-1/2 p-1 hover:bg-red-100 rounded-full text-grey hover:text-red-500 transition-colors ${isLogin && rememberedUsers.length > 0 ? 'right-10' : 'right-3'}`}
                      title={t("common:ph_epostaVeSifreyiTemizle")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {/* Dropdown toggle for remembered users */}
                  {isLogin && rememberedUsers.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowUserDropdown(!showUserDropdown)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-grey hover:text-navy"
                    >
                      <ChevronDown className={`w-5 h-5 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                  
                  {/* Remembered users dropdown */}
                  {showUserDropdown && rememberedUsers.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      <div className="p-2 border-b bg-slate-50">
                        <span className="text-xs text-grey font-medium">{t("common:hatirlananHesaplar")}</span>
                      </div>
                      {rememberedUsers.map((user, index) => (
                        <div
                          key={user.email}
                          className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => handleSelectRememberedUser(user)}
                          data-testid={`remembered-user-${index}`}
                        >
                          <div className="flex items-center gap-3">
                            <UserCircle className="w-8 h-8 text-navy" />
                            <div>
                              <p className="font-medium text-sm text-navy">{user.name}</p>
                              <p className="text-xs text-grey">
                                {user.email || user.phone || user.identifier || ""}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => handleRemoveRememberedUser(e, user.email)}
                            className="p-1 hover:bg-red-100 rounded-full text-grey hover:text-red-500 transition-colors"
                            title={t("common:ph_listedenKaldir")}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Telefon Alanı - sadece login modunda ve telefon auth seçiliyse */}
              {isLogin && authMethod === "phone" && (
              <div>
                <Label htmlFor="loginPhone">{t('common:phone')}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey z-10" />
                  <Input
                    id="loginPhone"
                    data-testid="login-phone-input"
                    type="tel"
                    placeholder="05XX XXX XX XX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-11 pl-10 rounded-xl"
                    required
                    autoComplete="off"
                  />
                </div>
              </div>
              )}

              <div>
                <Label htmlFor="password">{t('common:password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pl-10 pr-10 rounded-xl"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-grey hover:text-navy"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me - only for login */}
              {isLogin && (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-navy focus:ring-navy"
                    />
                    <label htmlFor="rememberMe" className="text-sm text-gray-600">
                      {t('auth:rememberMe')}
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowForgotPasswordModal(true)}
                    className="text-sm text-navy hover:underline font-medium"
                    data-testid="forgot-password-link"
                  >
                    {t('auth:forgotPassword')}
                  </button>
                </div>
              )}

              {/* DEV: Skip verification checkbox */}
              {/* Test Mode - Hidden for production. Re-enable by changing `&& false` to `&& true` */}
              {!isLogin && false && (
                <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <input
                    type="checkbox"
                    id="skipVerification"
                    checked={skipVerification}
                    onChange={(e) => setSkipVerification(e.target.checked)}
                    className="w-4 h-4 rounded border-yellow-400"
                  />
                  <label htmlFor="skipVerification" className="text-sm text-yellow-800">
                    {t('auth:testModeSkip')}
                  </label>
                </div>
              )}

              <Button
                type="submit"
                data-testid="submit-btn"
                className="w-full h-12 bg-navy hover:bg-navy/90 rounded-xl font-semibold"
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : isLogin ? (
                  t('auth:loginButton')
                ) : (
                  t('auth:registerButton')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-grey text-sm">
                {isLogin ? t('auth:noAccount') : t('auth:hasAccount')}{" "}
                <button
                  type="button"
                  data-testid="toggle-auth-mode"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    // Clear form fields when switching modes
                    setEmail("");
                    setPassword("");
                    setPhone("");
                    setName("");
                    setSurname("");
                    setGender("");
                    setBirthYear("");
                    setBirthMonth("");
                    setBirthDay("");
                    setKnownBranches([]);
                    setVerificationCode("");
                    setShowVerification(false);
                  }}
                  className="text-navy font-semibold hover:underline"
                >
                  {isLogin ? t('auth:registerButton') : t('auth:loginButton')}
                </button>
              </p>
            </div>

            {/* Legal Links */}
            <div className="mt-6 pt-4 border-t text-center">
              <p className="text-xs text-grey">
                {t('auth:termsText')}{" "}
                <Link to="/terms-of-service" className="text-navy hover:underline">
                  {t('auth:termsLink')}
                </Link>
                {" "}{t('auth:and')}{" "}
                <Link to="/privacy-policy" className="text-navy hover:underline">
                  {t('auth:privacyLink')}
                </Link>
                {t('auth:termsAccept')}
              </p>
            </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Phone Auth Modal */}
      <PhoneAuthModal
        isOpen={showPhoneAuth}
        onClose={() => {
          setShowPhoneAuth(false);
          setPhoneRegistrationData(null);
        }}
        registrationData={phoneRegistrationData}
      />
      
      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { if (!forgotSending) { setShowForgotPasswordModal(false); setForgotEmail(""); setForgotSentTo(null); } }}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="forgot-password-modal">
            <h2 className="text-xl font-semibold text-navy mb-2">{t('auth:forgotPassword')}</h2>
            {forgotSentTo ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-sm text-green-800">
                    ✉️ <strong>{forgotSentTo}</strong> adresine şifre sıfırlama bağlantısı gönderildi (eğer kayıtlıysa).
                  </p>
                  <p className="text-xs text-green-700 mt-2">
                    Bağlantı 30 dakika içinde geçerlidir. Gelen kutunuzu (ve istenmeyen klasörünü) kontrol edin.
                  </p>
                </div>
                <Button
                  type="button"
                  className="w-full h-11 bg-navy hover:bg-navy/90 rounded-xl"
                  onClick={() => { setShowForgotPasswordModal(false); setForgotEmail(""); setForgotSentTo(null); }}
                  data-testid="forgot-password-close"
                >
                  Tamam
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Hesabınıza kayıtlı e-posta adresinizi girin. Şifrenizi sıfırlamak için bir bağlantı göndereceğiz.
                </p>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-navy focus:ring-2 focus:ring-navy/20 outline-none mb-4"
                  data-testid="forgot-password-email"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-11 rounded-xl"
                    onClick={() => { setShowForgotPasswordModal(false); setForgotEmail(""); }}
                    disabled={forgotSending}
                  >
                    İptal
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 h-11 bg-navy hover:bg-navy/90 rounded-xl"
                    disabled={forgotSending || !forgotEmail}
                    data-testid="forgot-password-submit"
                    onClick={async () => {
                      if (!forgotEmail.trim()) return;
                      setForgotSending(true);
                      try {
                        const r = await fetch(`${API}/auth/password-reset/request`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            email: forgotEmail.trim(),
                            frontend_url: window.location.origin,
                          }),
                        });
                        if (r.ok) {
                          setForgotSentTo(forgotEmail.trim());
                        } else {
                          const err = await r.json().catch(() => ({}));
                          toast.error(err.detail || "Bir hata oluştu, lütfen tekrar deneyin");
                        }
                      } catch (e) {
                        toast.error("Bağlantı hatası, lütfen tekrar deneyin");
                      } finally {
                        setForgotSending(false);
                      }
                    }}
                  >
                    {forgotSending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      "Bağlantı Gönder"
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
